import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  type AgentRunDetailResponse,
  type AgentRunSummary,
  useAgentRun,
} from '~/entities/document-agent'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { getApiErrorStatus, retryUnlessClientError } from '~/shared/lib/api-error'
import { Alert, Button, SkeletonBlock } from '~/shared/ui'
import { buildReview, buildTimeline } from '../lib'
import { useCategoryDescriber } from '../model'
import { DocumentPane } from './document-pane/document-pane'
import { FinalStateSection } from './final-state-section'
import { PatientCaseSection } from './patient-case-section'
import { RunDetailsSection } from './run-details-section'
import { TimelineSection } from './timeline-section'
import { VerdictHero } from './verdict-hero'

export function RunDetailSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <SkeletonBlock className="h-10 w-[480px]" />
      <div className="grid grid-cols-3 gap-3">
        <SkeletonBlock className="h-40" />
        <SkeletonBlock className="h-40" />
        <SkeletonBlock className="h-40" />
      </div>
      <SkeletonBlock className="h-72 w-full" />
      <SkeletonBlock className="h-96 w-full" />
    </div>
  )
}

function RunStory({
  documentKey,
  data,
  otherRuns,
  onSelectRun,
}: {
  documentKey: string
  data: AgentRunDetailResponse
  otherRuns: AgentRunSummary[]
  onSelectRun: (runId: string) => void
}) {
  const { run, transcript, match_transcript: matchTranscript, comparison } = data
  const describeCategory = useCategoryDescriber()
  const history = useQuery({
    ...apiClient.getDocumentAuditTrailDocumentsKeyAuditTrailGetOptions({
      path: { key: documentKey },
    }),
    retry: retryUnlessClientError,
  })

  const review = useMemo(
    () => buildReview({ run, comparison }, describeCategory),
    [run, comparison, describeCategory],
  )
  const entries = useMemo(
    () =>
      buildTimeline({
        events: history.data?.events ?? [],
        run,
        otherRuns,
        hasClassification: true,
        hasMatching: !!matchTranscript || !!run.match?.session,
        context: {
          categoryLabel: (slug) => describeCategory(slug).label ?? slug,
          finalCategory: comparison?.final_category ?? null,
          finalCodes: comparison?.reviewer_tags ?? null,
          reviewed: review.reviewed,
        },
      }),
    [history.data, run, otherRuns, matchTranscript, comparison, describeCategory, review.reviewed],
  )
  const transcripts = useMemo(() => [transcript, matchTranscript], [transcript, matchTranscript])

  return (
    <div className="flex min-h-0 flex-1">
      <div className="bg-surface-low min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[960px] flex-col gap-10 px-6 pt-6 pb-16">
          {run.error && !!run.final && (
            <Alert
              className="grow-0"
              appearance="ERROR"
              materialIcon="error"
            >
              {run.error}
            </Alert>
          )}
          <VerdictHero
            run={run}
            review={review}
          />
          <FinalStateSection
            run={run}
            review={review}
          />
          <PatientCaseSection
            run={run}
            review={review}
            comparison={comparison?.matching ?? null}
          />
          <TimelineSection
            entries={entries}
            run={run}
            transcript={transcript}
            matchTranscript={matchTranscript}
            review={review}
            onSelectRun={onSelectRun}
            isHistoryLoading={history.isPending}
            historyError={history.isError}
          />
          <RunDetailsSection run={run} />
        </div>
      </div>
      <DocumentPane
        documentKey={documentKey}
        transcripts={transcripts}
      />
    </div>
  )
}

export function RunDetail({
  documentKey,
  runId,
  runs,
  onSelectRun,
}: {
  documentKey: string
  runId: string
  runs: AgentRunSummary[]
  onSelectRun: (runId: string) => void
}) {
  const { data, error, isPending, isError, refetch } = useAgentRun(documentKey, runId)
  const otherRuns = useMemo(() => runs.filter((run) => run.run_id !== runId), [runs, runId])

  if (isPending) {
    return (
      <div className="bg-surface-low flex-1 overflow-hidden p-6">
        <div className="mx-auto max-w-[960px]">
          <RunDetailSkeleton />
        </div>
      </div>
    )
  }

  if (isError) {
    const status = getApiErrorStatus(error)
    return (
      <div className="flex flex-col items-start gap-3 p-6">
        <Alert
          className="grow-0"
          appearance="ERROR"
          materialIcon="error"
        >
          {status === 404
            ? `Run ${runId} was not found for this document.`
            : status === 403
              ? 'Agent runs are not available for your account.'
              : 'Could not load this run.'}
        </Alert>
        {status !== 404 && status !== 403 && <Button onClick={() => refetch()}>Try again</Button>}
      </div>
    )
  }

  return (
    <RunStory
      documentKey={documentKey}
      data={data}
      otherRuns={otherRuns}
      onSelectRun={onSelectRun}
    />
  )
}
